import { useEffect, useRef, useState } from "react"
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import PersonOutlineIcon from "@mui/icons-material/PersonOutline"
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { splitHue } from "./leadRoutingUi"
import { consultantMatchesTagConditions } from "./ruleMembership"
import {
  ConsultantSummary,
  DistributionGroup,
  RequiredTagsMode,
  TagCondition,
} from "@features/lead-routing/leadRouting.types"

const TAG_MODE_LABELS: Record<RequiredTagsMode, string> = {
  any: "has any of",
  all: "has all of",
  none: "has none of",
}

// A distribution row while editing: a stable uid (so a drag reorders the row,
// not just its slot) and colorSeed (so its colour follows the row). A group row
// gates on a stack of tag conditions (all AND'd); a consultant row names one
// person. kind decides which it is while still empty.
export interface EditableGroup {
  uid: string
  colorSeed: number
  kind: "group" | "consultant"
  conditions: TagCondition[]
  consultantIds: string[]
  weight: number
}

let uidCounter = 0
const nextUid = () => `g${uidCounter++}-${Math.round(Math.random() * 1e6)}`

export function fromDistributionGroups(
  groups: DistributionGroup[],
): EditableGroup[] {
  return groups.map((group, index) => ({
    uid: nextUid(),
    colorSeed: index,
    kind: (group.consultantIds?.length ?? 0) > 0 ? "consultant" : "group",
    conditions:
      group.conditions.length > 0
        ? group.conditions
        : [{ mode: "any", tags: [] }],
    consultantIds: group.consultantIds ?? [],
    weight: group.weight,
  }))
}

export function toDistributionGroups(
  groups: EditableGroup[],
): DistributionGroup[] {
  return groups
    .map((group) => ({
      conditions: group.conditions.filter((c) => c.tags.length > 0),
      consultantIds: group.consultantIds,
      weight: round1(group.weight),
    }))
    .filter(
      (group) => group.conditions.length > 0 || group.consultantIds.length > 0,
    )
}

function round1(value: number): number {
  return Math.round(value * 10) / 10
}

// The left column (drag handle, colour, and the tag conditions or name) is fixed
// so every slider starts at the same x however tall a group's condition stack.
const LEFT_COL = 360

function hasGate(conditions: TagCondition[]): boolean {
  return conditions.some((c) => c.tags.length > 0)
}

// Place each eligible consultant into EVERY row that targets them — meeting
// all its tag conditions, or being its named consultant. A person in several
// rows gets the sum of their per-row shares (that's how nested tiers work).
// Anyone in no row is returned to warn.
export function placeConsultants(
  groups: EditableGroup[],
  consultants: ConsultantSummary[],
): { perGroup: ConsultantSummary[][]; ungrouped: ConsultantSummary[] } {
  const idSets = groups.map((group) => new Set(group.consultantIds))
  const perGroup: ConsultantSummary[][] = groups.map(() => [])
  const ungrouped: ConsultantSummary[] = []
  for (const consultant of consultants) {
    let placed = false
    groups.forEach((group, i) => {
      if (
        idSets[i].has(consultant.id) ||
        (hasGate(group.conditions) &&
          consultantMatchesTagConditions(consultant.tags, group.conditions))
      ) {
        perGroup[i].push(consultant)
        placed = true
      }
    })
    if (!placed) {
      ungrouped.push(consultant)
    }
  }
  return { perGroup, ungrouped }
}

function ConditionStack(props: {
  conditions: TagCondition[]
  allTags: string[]
  onChange: (conditions: TagCondition[]) => void
}) {
  const { conditions, allTags, onChange } = props
  const patch = (index: number, next: Partial<TagCondition>) =>
    onChange(conditions.map((c, i) => (i === index ? { ...c, ...next } : c)))
  const remove = (index: number) =>
    onChange(conditions.filter((_, i) => i !== index))
  return (
    <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
      {conditions.map((condition, index) => (
        <Stack key={index} direction="row" spacing={0.5} alignItems="center">
          {index > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ pr: 0.5, textTransform: "none" }}
            >
              and
            </Typography>
          )}
          <TextField
            select
            size="small"
            variant="standard"
            value={condition.mode}
            onChange={(e) =>
              patch(index, { mode: e.target.value as RequiredTagsMode })
            }
            sx={{ width: 96, flexShrink: 0 }}
          >
            {(Object.keys(TAG_MODE_LABELS) as RequiredTagsMode[]).map(
              (mode) => (
                <MenuItem key={mode} value={mode} sx={{ fontSize: 13 }}>
                  {TAG_MODE_LABELS[mode]}
                </MenuItem>
              ),
            )}
          </TextField>
          <Autocomplete
            multiple
            size="small"
            options={allTags}
            value={condition.tags}
            onChange={(_, value) => patch(index, { tags: value })}
            renderTags={(value, getTagProps) =>
              value.map((tag, i) => (
                <Chip
                  {...getTagProps({ index: i })}
                  key={tag}
                  label={tag}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, "& .MuiChip-label": { px: 0.75 } }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} placeholder="tags" variant="standard" />
            )}
            sx={{ flex: 1, minWidth: 0 }}
          />
          {conditions.length > 1 && (
            <IconButton
              size="small"
              aria-label="remove condition"
              onClick={() => remove(index)}
            >
              <DeleteOutlineIcon sx={{ fontSize: 15 }} />
            </IconButton>
          )}
        </Stack>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon sx={{ fontSize: 14 }} />}
        onClick={() => onChange([...conditions, { mode: "any", tags: [] }])}
        sx={{ alignSelf: "flex-start", fontSize: 12, py: 0, minWidth: 0 }}
      >
        and
      </Button>
    </Stack>
  )
}

function GroupRow(props: {
  group: EditableGroup
  members: ConsultantSummary[]
  remaining: number
  selected: boolean
  allTags: string[]
  personName: string | null
  addableConsultants: ConsultantSummary[]
  onToggleSelect: (additive: boolean) => void
  onConditions: (conditions: TagCondition[]) => void
  onConsultant: (id: string) => void
  onWeight: (value: number) => void
  onRemove: () => void
}) {
  const {
    group,
    members,
    remaining,
    selected,
    allTags,
    personName,
    addableConsultants,
    onToggleSelect,
    onConditions,
    onConsultant,
    onWeight,
    onRemove,
  } = props
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.uid })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }
  // Dragged fully right, the slider fills the row to 100% — it stops at the
  // headroom left, so it never pushes the total past 100 or moves other rows.
  const cap = Math.min(100, group.weight + Math.max(0, remaining))
  const perHead = members.length > 0 ? group.weight / members.length : 0
  const hue = splitHue(group.colorSeed)
  const isPerson = group.kind === "consultant"

  return (
    <Box
      ref={setNodeRef}
      style={style}
      // Shift/⌘-click anywhere on the row selects it (a plain click still works
      // the controls). Selected rows move their shares together.
      onClick={(e) => {
        if (e.shiftKey || e.metaKey || e.ctrlKey) {
          e.preventDefault()
          onToggleSelect(true)
        }
      }}
      sx={{
        py: 1,
        px: 0.5,
        mx: -0.5,
        borderRadius: 1,
        borderTop: "1px solid",
        borderColor: "divider",
        "&:first-of-type": { borderTop: "none" },
        bgcolor: selected ? "action.selected" : "transparent",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box
          sx={{
            width: LEFT_COL,
            flexShrink: 0,
            display: "flex",
            gap: 0.5,
            alignItems: "flex-start",
            pt: 0.5,
          }}
        >
          <IconButton
            size="small"
            aria-label="drag to reorder"
            sx={{ cursor: "grab", touchAction: "none" }}
            {...attributes}
            {...listeners}
          >
            <DragIndicatorIcon fontSize="small" color="disabled" />
          </IconButton>
          <Box
            sx={{
              width: 10,
              height: 10,
              mt: 1.25,
              borderRadius: "3px",
              flexShrink: 0,
              bgcolor: hue.fill,
            }}
          />
          {isPerson ? (
            group.consultantIds.length > 0 ? (
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ flex: 1, minWidth: 0, pl: 0.75, pt: 0.5 }}
              >
                <PersonOutlineIcon fontSize="small" color="disabled" />
                <Typography variant="body2" noWrap title={personName ?? ""}>
                  {personName ?? "Unknown"}
                </Typography>
              </Stack>
            ) : (
              <Autocomplete
                size="small"
                options={addableConsultants}
                getOptionLabel={(option) => option.name}
                value={null}
                onChange={(_, option) => option && onConsultant(option.id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="choose a consultant"
                    variant="standard"
                  />
                )}
                sx={{ flex: 1, minWidth: 0, pl: 0.5 }}
              />
            )
          ) : (
            <Box sx={{ flex: 1, minWidth: 0, pl: 0.25 }}>
              <ConditionStack
                conditions={group.conditions}
                allTags={allTags}
                onChange={onConditions}
              />
            </Box>
          )}
        </Box>

        <Slider
          size="small"
          value={group.weight}
          min={0}
          max={100}
          step={1}
          onChange={(_, value) => onWeight(Math.min(value as number, cap))}
          sx={{ flex: 1, mt: 1, "& .MuiSlider-rail": { opacity: 0.3 } }}
        />
        <TextField
          size="small"
          type="number"
          value={round1(group.weight)}
          onChange={(e) => onWeight(Number(e.target.value))}
          InputProps={{ endAdornment: <span>%</span> }}
          sx={{ width: 84, flexShrink: 0 }}
        />
        <Tooltip title="Remove this row">
          <IconButton
            size="small"
            aria-label="remove row"
            onClick={onRemove}
            sx={{ flexShrink: 0, mt: 0.5 }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: "block",
          pl: `${LEFT_COL - 320}px`,
          textTransform: "none",
        }}
      >
        {/* Per-row amounts only: a person in several rows gets the sum, and
            the "In several rows" summary below carries the combined totals. */}
        {isPerson
          ? group.consultantIds.length > 0
            ? group.weight === 0
              ? "0% — this row adds nothing"
              : `gets ${round1(group.weight)}% from this row`
            : ""
          : members.length === 0
          ? "no one matches yet"
          : group.weight === 0
          ? `${members
              .map((m) => m.name)
              .join(", ")} · 0% — this row adds nothing`
          : `${members.map((m) => m.name).join(", ")} · ${round1(
              perHead,
            )}% each from this row`}
      </Typography>
    </Box>
  )
}

// A rule's distribution as one view: a list of rows, each taking a share (rows
// total 100%) split evenly among whoever it targets. A group gates on stacked
// tag conditions (all AND'd); a consultant row names one person. A person in
// several rows gets the sum of their per-row shares (nested tiers rely on
// this); a summary under the rows shows those combined totals. Sliders are
// capped so one never pushes the total past 100 or moves the others; drag one
// fully right to fill a row. Shift/⌘-click rows to move several by one amount.
export function DistributionGroupsEditor(props: {
  groups: EditableGroup[]
  eligible: ConsultantSummary[]
  allTags: string[]
  onChange: (groups: EditableGroup[]) => void
}) {
  const { groups, eligible, allTags, onChange } = props
  const sensors = useSensors(useSensor(PointerSensor))
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const nameById = new Map(eligible.map((c) => [c.id, c.name]))

  // Scroll a freshly-added row into view — it appends at the bottom, often
  // below the fold of the scrollable dialog.
  const bottomRef = useRef<HTMLDivElement>(null)
  const [scrollPending, setScrollPending] = useState(false)
  useEffect(() => {
    if (scrollPending) {
      bottomRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      })
      setScrollPending(false)
    }
  }, [scrollPending, groups])

  const { perGroup, ungrouped } = placeConsultants(groups, eligible)
  // Combined totals for anyone in more than one row, so a nested setup's
  // effective per-person share is visible at a glance.
  const combinedTotals = new Map<
    string,
    { name: string; rows: number; total: number }
  >()
  perGroup.forEach((members, i) => {
    const perHead = members.length > 0 ? groups[i].weight / members.length : 0
    for (const member of members) {
      const entry = combinedTotals.get(member.id) ?? {
        name: member.name,
        rows: 0,
        total: 0,
      }
      entry.rows += 1
      entry.total += perHead
      combinedTotals.set(member.id, entry)
    }
  })
  const multiRowTotals = [...combinedTotals.values()]
    .filter((entry) => entry.rows > 1)
    .sort((a, b) => b.total - a.total)
  const total = groups.reduce((sum, group) => sum + group.weight, 0)
  const remaining = 100 - total
  const atHundred = Math.abs(total - 100) < 0.5

  const setGroup = (uid: string, patch: Partial<EditableGroup>) =>
    onChange(groups.map((g) => (g.uid === uid ? { ...g, ...patch } : g)))

  const setWeight = (uid: string, value: number) => {
    const target = Math.max(0, Math.min(100, value))
    const movingTogether = selected.has(uid) && selected.size > 1
    if (!movingTogether) {
      const others = total - (groups.find((g) => g.uid === uid)?.weight ?? 0)
      const capped = Math.max(0, Math.min(target, 100 - others))
      onChange(
        groups.map((g) => (g.uid === uid ? { ...g, weight: capped } : g)),
      )
      return
    }
    const sel = groups.filter((g) => selected.has(g.uid))
    let delta = target - (groups.find((g) => g.uid === uid)?.weight ?? 0)
    if (delta > 0) {
      delta = Math.min(delta, remaining / sel.length)
    } else {
      delta = Math.max(delta, -Math.min(...sel.map((g) => g.weight)))
    }
    onChange(
      groups.map((g) =>
        selected.has(g.uid)
          ? { ...g, weight: Math.max(0, Math.min(100, g.weight + delta)) }
          : g,
      ),
    )
  }

  const toggleSelect = (uid: string, additive: boolean) =>
    setSelected((prev) => {
      const next = additive ? new Set(prev) : new Set<string>()
      if (additive && prev.has(uid)) {
        next.delete(uid)
      } else {
        next.add(uid)
      }
      return next
    })

  const nextColorSeed = () =>
    groups.reduce((max, g) => Math.max(max, g.colorSeed), -1) + 1

  const addGroup = () => {
    onChange([
      ...groups,
      {
        uid: nextUid(),
        colorSeed: nextColorSeed(),
        kind: "group",
        conditions: [{ mode: "any", tags: [] }],
        consultantIds: [],
        weight: 0,
      },
    ])
    setScrollPending(true)
  }

  const addConsultant = (id: string) => {
    onChange([
      ...groups,
      {
        uid: nextUid(),
        colorSeed: nextColorSeed(),
        kind: "consultant",
        conditions: [],
        consultantIds: [id],
        weight: 0,
      },
    ])
    setScrollPending(true)
  }

  const distributeEvenly = () =>
    groups.length &&
    onChange(groups.map((g) => ({ ...g, weight: 100 / groups.length })))

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }
    const from = groups.findIndex((g) => g.uid === active.id)
    const to = groups.findIndex((g) => g.uid === over.id)
    if (from >= 0 && to >= 0) {
      onChange(arrayMove(groups, from, to))
    }
  }

  // A consultant may sit in several rows (their shares sum), so everyone stays
  // addable.
  const addableConsultants = eligible

  return (
    <Box>
      {/* Controls sit at the top, right under the conditions. */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" startIcon={<AddIcon />} onClick={addGroup}>
            Add group
          </Button>
          <Autocomplete
            size="small"
            options={addableConsultants}
            getOptionLabel={(option) => option.name}
            value={null}
            blurOnSelect
            onChange={(_, option) => option && addConsultant(option.id)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                placeholder="Add a consultant…"
              />
            )}
            sx={{ width: 180 }}
          />
          <Button
            size="small"
            onClick={distributeEvenly}
            disabled={!groups.length}
          >
            Distribute evenly
          </Button>
        </Stack>
        <Typography
          variant="body2"
          color={atHundred ? "success.main" : "text.secondary"}
        >
          {atHundred
            ? "✓ 100%"
            : `${round1(total)}% allocated · ${round1(
                Math.max(0, remaining),
              )}% left`}
        </Typography>
      </Stack>

      {/* Split bar: rows as coloured segments, unallocated space striped. */}
      <Box sx={{ display: "flex", height: 26, gap: "4px", mb: 1 }}>
        {groups.map((group) => {
          const hue = splitHue(group.colorSeed)
          return (
            <Box
              key={group.uid}
              sx={{
                width: `${Math.max(group.weight, 0)}%`,
                bgcolor: "background.paper",
                color: hue.text,
                border: "1px solid",
                borderColor: "divider",
                borderBottom: `3px solid ${hue.line}`,
                borderRadius: "6px 6px 4px 4px",
                display: "grid",
                placeItems: "center",
                fontSize: 10,
                overflow: "hidden",
              }}
            >
              {group.weight >= 9 ? `${round1(group.weight)}%` : ""}
            </Box>
          )
        })}
        {remaining > 0.5 && (
          <Box
            sx={{
              width: `${remaining}%`,
              borderRadius: "4px",
              backgroundImage:
                "repeating-linear-gradient(45deg, rgba(0,0,0,0.06) 0 6px, transparent 6px 12px)",
              border: "1px dashed",
              borderColor: "divider",
            }}
          />
        )}
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={groups.map((g) => g.uid)}
          strategy={verticalListSortingStrategy}
        >
          {groups.map((group, index) => (
            <GroupRow
              key={group.uid}
              group={group}
              members={perGroup[index] ?? []}
              remaining={remaining}
              selected={selected.has(group.uid)}
              allTags={allTags}
              addableConsultants={addableConsultants}
              personName={
                group.consultantIds.length > 0
                  ? nameById.get(group.consultantIds[0]) ?? "Unknown"
                  : null
              }
              onToggleSelect={(additive) => toggleSelect(group.uid, additive)}
              onConditions={(conditions) => setGroup(group.uid, { conditions })}
              onConsultant={(id) =>
                setGroup(group.uid, { consultantIds: [id] })
              }
              onWeight={(value) => setWeight(group.uid, value)}
              onRemove={() => {
                setSelected(new Set())
                onChange(groups.filter((g) => g.uid !== group.uid))
              }}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Box ref={bottomRef} />

      {groups.length === 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", py: 1 }}
        >
          Add a group or a consultant to route this rule&apos;s leads to.
        </Typography>
      )}

      <Stack
        direction="row"
        spacing={1.5}
        sx={{ mt: 1, flexWrap: "wrap" }}
        alignItems="center"
      >
        {groups.length > 1 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: "none" }}
          >
            A person in several rows gets the sum of their shares. Shift or
            ⌘-click rows to move their shares together.
          </Typography>
        )}
        {selected.size > 1 && (
          <Typography variant="caption" color="primary.main">
            Moving {selected.size} together{" · "}
            <Box
              component="span"
              onClick={() => setSelected(new Set())}
              sx={{ cursor: "pointer" }}
            >
              clear
            </Box>
          </Typography>
        )}
      </Stack>

      {multiRowTotals.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.5, textTransform: "none" }}
        >
          In several rows:{" "}
          {multiRowTotals
            .map((entry) => `${entry.name} ${round1(entry.total)}% combined`)
            .join(" · ")}
        </Typography>
      )}

      {ungrouped.length > 0 && (
        <Typography
          variant="caption"
          color="warning.main"
          sx={{ display: "block", mt: 0.5, textTransform: "none" }}
        >
          {ungrouped.length === 1
            ? `${ungrouped[0].name} matches this rule but isn't in any row, so they'll get no leads.`
            : `${ungrouped.length} consultants match this rule but aren't in any row, so they'll get no leads.`}
        </Typography>
      )}
    </Box>
  )
}
